import { Link, useParams } from 'react-router';
import { addDays, countsInSpending, fundStatus, inRange, spendAmount } from '@tahi/core';
import { setDefaultFund } from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Money, ScreenHeader } from '../../../ui';
import { spokenPeso } from '../../../ui/spokenPeso';
import { sourceLabel } from '../../spending/format';
import { copy, errorText, leftText } from '../copy';
import { dayShort, periodLabel, rangeShort, spokenDate } from '../format';
import { accountStatusesOf } from '../derive';
import { useBudgetToday } from '../hooks';
import { CARD, FOCUS, LABEL, QUIET_LINK, SCREEN } from '../styles';

export function FundDetailScreen() {
  const { id = '' } = useParams();
  const env = useCommandEnv();
  const s = useBudgetToday();
  if (!s) {
    return (
      <main className={SCREEN}>
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }
  const fund = s.funds.find((f) => f.id === id);
  if (!fund) {
    return (
      <main className={SCREEN}>
        <p className="text-muted">{errorText('not_found')}</p>
      </main>
    );
  }
  const st = fundStatus(fund, s.entries, s.today, { weekStart: s.weekStart, sources: s.sources });
  const next = addDays(st.range.end, 1);
  const rows = s.entries
    .filter((e) => e.fundId === id && countsInSpending(e) && inRange(e.date, st.range))
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  const isDefault = s.settings.defaultFundId === id;
  // v0.5: the accounts the money is kept in, with their combined balance.
  const statuses = accountStatusesOf(s);
  const keptIn = fund.accountIds
    .map((aid) => statuses.find((x) => x.account.id === aid))
    .filter((x): x is NonNullable<typeof x> => x !== undefined);

  return (
    <main className={SCREEN}>
      <ScreenHeader
        title={fund.name}
        back={{ to: '/m/budget/funds', label: copy.funds }}
        actions={
          <Link to={`/m/budget/funds/${id}/edit`} className={QUIET_LINK}>
            {copy.editFund}
          </Link>
        }
      />
      <section
        aria-label={copy.fundCard(fund.name, spokenPeso(st.leftMinor), spokenDate(next))}
        className={`${CARD} flex flex-col gap-1`}
      >
        <span data-testid="fund-left">
          <Money
            minor={Math.abs(st.leftMinor)}
            cents="auto"
            animate={false}
            className="text-[40px] leading-tight"
          />
        </span>
        <p className="text-sm text-muted">{leftText(st.leftMinor, next)}</p>
        <p className="text-sm text-muted">{copy.spentOf(st.spentMinor, fund.amountMinor)}</p>
        <p className="text-sm text-muted">
          {copy.fundPeriodLine(periodLabel(fund.period), rangeShort(st.range))}
        </p>
        {keptIn.length > 0 ? (
          <p className="text-sm text-muted">
            {copy.keptIn}: {keptIn.map((a) => a.account.name).join(', ')} ·{' '}
            {copy.keptInTotal(keptIn.reduce((sum, a) => sum + a.balanceMinor, 0))}
          </p>
        ) : null}
        {isDefault ? (
          <p className={LABEL}>{copy.defaultBadge}</p>
        ) : (
          <Button
            variant="secondary"
            className="mt-2 self-start"
            onClick={() => void setDefaultFund(env, id)}
          >
            {copy.makeDefault}
          </Button>
        )}
      </section>
      <h2 className="text-[20px] font-bold text-fg">{copy.fundEntries}</h2>
      {rows.length === 0 ? (
        <p className="text-muted">{copy.noFundEntries}</p>
      ) : (
        <ul aria-label={copy.fundEntries} className="divide-y divide-divider">
          {rows.map((e) => (
            <li key={e.id}>
              <Link
                to={`/m/spending/entries/${e.id}`}
                className={`flex min-h-11 items-center justify-between gap-3 ${FOCUS}`}
              >
                <span className="text-fg">
                  {e.note ?? sourceLabel(e.sourceModule)} · {dayShort(e.date)}
                </span>
                <Money minor={spendAmount(e)} cents="auto" animate={false} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
